/**
 * Tabbed Left Panel Component
 * 
 * Unified left panel with tabs for:
 * - Segments (SourcePanel)
 * - Video Settings (VideoSettingsPanel)
 */

import React, { useState } from 'react';
import SourcePanel from './SourcePanel';
import VideoSettingsPanel from './VideoSettingsPanel';
import { SourcePanelProps } from '../types';
import { OverlayConfig } from '../../../types/overlayConfig';

interface TabbedLeftPanelProps extends Omit<SourcePanelProps, 'sources'> {
  sources: SourcePanelProps['sources'];
  overlayConfig?: OverlayConfig;
  onOverlayConfigChange?: (config: OverlayConfig) => void;
  matchId?: string;
}

const TabbedLeftPanel: React.FC<TabbedLeftPanelProps> = ({
  sources,
  onAddClip,
  onAddClips,
  rangeBefore,
  rangeAfter,
  overlayConfig,
  onOverlayConfigChange,
  matchId,
}) => {
  const [activeTab, setActiveTab] = useState<'segments' | 'settings'>('segments');

  return (
    <div className="flex flex-col h-full bg-gray-50">
      {/* Tabs */}
      <div className="flex border-b border-gray-200 bg-white">
        <button
          onClick={() => setActiveTab('segments')}
          className={`flex-1 px-3 py-2 text-xs font-medium transition-colors border-b-2 ${
            activeTab === 'segments'
              ? 'border-blue-600 text-blue-600 bg-blue-50'
              : 'border-transparent text-gray-600 hover:text-gray-900 hover:bg-gray-50'
          }`}
        >
          Segments
        </button>
        <button
          onClick={() => setActiveTab('settings')}
          className={`flex-1 px-3 py-2 text-xs font-medium transition-colors border-b-2 ${
            activeTab === 'settings'
              ? 'border-blue-600 text-blue-600 bg-blue-50'
              : 'border-transparent text-gray-600 hover:text-gray-900 hover:bg-gray-50'
          }`}
        >
          Video Settings
        </button>
      </div>

      {/* Tab Content */}
      <div className="flex-1 overflow-hidden">
        {activeTab === 'segments' ? (
          <SourcePanel
            sources={sources}
            onAddClip={onAddClip}
            onAddClips={onAddClips}
            rangeBefore={rangeBefore}
            rangeAfter={rangeAfter}
          />
        ) : (
          <VideoSettingsPanel
            overlayConfig={overlayConfig}
            onOverlayConfigChange={onOverlayConfigChange}
            matchId={matchId || ''}
          />
        )}
      </div>
    </div>
  );
};

export default TabbedLeftPanel;
